import * as React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import type {
  AsyncLoadOptions,
  PaginatedFilterProps,
  SingleOption,
  SelectOption,
} from '@atlassian/paginated-filter';
import PaginatedFilter from '@atlassian/paginated-filter';

import { useAppUpdatesContext } from '@app/app/src/AppContext';
import { useAnalyticsEvent, appUpdatesUIEvents } from '@app/analytics';
import { IMPACT_RATING_DISPLAY_NAMES, IMPACT_RATING_IDS } from '../constants';
import type { ImpactRating } from '../types';

export const messages = defineMessages({
  impactRating: {
    id: 'appUpdates.filters.impactRating.label',
    description: 'Label for the impact rating filter',
    defaultMessage: 'Impact rating',
  },
});

// Defined outside component to maintain stable references — same pattern as ProductFilter
const impactRatingOptions: ImpactRating[] = IMPACT_RATING_IDS.map((id) => ({
  id,
  name: IMPACT_RATING_DISPLAY_NAMES[id],
}));

export const useImpactRatingFilterProps = (): PaginatedFilterProps => {
  const { formatMessage } = useIntl();
  const { state, dispatch } = useAppUpdatesContext();
  const { sendUIEvent } = useAnalyticsEvent();

  const impactRatingSelectOptions: SingleOption[] = impactRatingOptions.map(
    (rating): SingleOption => ({
      label: rating.name,
      value: rating.id,
      optionType: 'option',
    }),
  );

  const getSelectedRatings = (): SingleOption[] => {
    const selectedImpactRatings =
      state.updates.draftFilters.selectedImpactRatings;

    if (!selectedImpactRatings || selectedImpactRatings.length === 0) {
      return [];
    }

    return selectedImpactRatings
      .map((selectedRating) =>
        impactRatingSelectOptions.find(
          (option) => option.value === selectedRating.id,
        ),
      )
      .filter((option): option is SingleOption => option !== undefined);
  };

  const onImpactRatingFilterChange = (options: readonly SingleOption[]) => {
    const selectedRatingValues = options
      .map((option) =>
        impactRatingOptions.find((candidate) => candidate.id === option.value),
      )
      .filter((rating): rating is ImpactRating => rating !== undefined);

    dispatch({
      type: 'FILTER_IMPACT_RATING_SELECT',
      payload: {
        selectedImpactRatings:
          selectedRatingValues.length > 0 ? selectedRatingValues : null,
      },
    });

    // Send analytics event
    const updatesImpactTypes =
      selectedRatingValues.length > 0
        ? selectedRatingValues.map((r) => r.id).join(',')
        : undefined;
    sendUIEvent(
      appUpdatesUIEvents.ui.appUpdatesImpactRatingFilterClicked(
        { source: 'appUpdatesListScreen' },
        updatesImpactTypes,
      ).data,
    );
  };

  const onLoadOptions: AsyncLoadOptions = (
    search,
    _enableFullList,
    _showFullList,
    _showMore,
    _lastOption,
    callback,
  ) => {
    let options: SelectOption[] = impactRatingSelectOptions.map((option) => ({
      label: option.label,
      value: option.value,
      optionType: 'option',
    }));

    if (search) {
      const searchText = search.toLowerCase();
      options = options.filter((option) =>
        option.label?.toLowerCase().includes(searchText),
      );
    }

    callback({
      options,
      totalCount: options.length,
    });
  };

  return {
    testId: 'app-updates-filter-impact-rating-testid',
    fieldLabel: formatMessage(messages.impactRating),
    fieldId: 'app-updates-table-impact-rating-filter',
    onLoadOptions,
    value: getSelectedRatings(),
    onChange: onImpactRatingFilterChange,
    onClearValues: () => onImpactRatingFilterChange([]),
  };
};

export const ImpactRatingFilter: React.FC = () => {
  const filterProps = useImpactRatingFilterProps();

  return <PaginatedFilter {...filterProps} />;
};
